/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate } from 'react-router-dom';
import { FaPlus } from 'react-icons/fa';
import {
  Container,
  Card,
  Button,
  Form,
  Row,
  Col,
  Pagination,
} from 'react-bootstrap';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';
import 'moment/locale/pt-br';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import axios from '../../services/axios';
import Loading from '../../components/Loading';
import Breadcrumbs from '../../components/Breadcrumbs';
import DataTable from '../../components/DataTable';
import ErrorProcessor from '../../components/ErrorProcessor';
import CardHeaderMore from '../../components/Cards/CardHeaderMore';
import { useNotification } from '../../components/Notification/NotificationContext';

moment.locale('pt-br');

// A listagem trabalha apenas com tickets em andamento. Fechados e cancelados
// são descartados no banco, e não depois de trafegar até o navegador.
const OPEN_STATUSES = ['aberto', 'em_atendimento', 'pausado'];

// --- COMPONENTES AUXILIARES (SEM ALTERAÇÕES) ---
function TicketNumberCell({ row }) {
  const { id } = row.original;
  return <Link to={`/tickets/${id}/edit`}>#{id}</Link>;
}
TicketNumberCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    }).isRequired,
  }).isRequired,
};

const ticketShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  title: PropTypes.string.isRequired,
  requester: PropTypes.string,
  nomeEmpresa: PropTypes.string,
  priority: PropTypes.string,
  assignedTo: PropTypes.string,
  vencAtendimento: PropTypes.string,
  vencSolucao: PropTypes.string,
  status: PropTypes.string.isRequired,
});

function KanbanTicketCard({ ticket }) {
  const getStatusDisplay = (status) => {
    switch (status) {
      case 'aberto':
        return <span className="text-success fw-bold">Aberto</span>;
      case 'em_atendimento':
        return <span className="text-warning fw-bold">Em Atendimento</span>;
      case 'pausado':
        return <span className="text-primary fw-bold">Em Pausa</span>;
      case 'fechado':
        return <span className="text-danger fw-bold">Fechado</span>;
      default:
        return <span className="text-secondary fw-bold">{status}</span>;
    }
  };

  return (
    <Card className="mb-3 kanban-ticket-card border shadow-sm">
      <Card.Body>
        <Card.Title className="h6 mb-2">
          <Link
            to={`/tickets/${ticket.id}/edit`}
            className="text-decoration-none"
          >
            #{ticket.id} - {ticket.title}
          </Link>
        </Card.Title>
        <Card.Text className="small mb-1">
          <strong>Solicitante:</strong> {ticket.requester}
        </Card.Text>
        <Card.Text className="small mb-1">
          <strong>Empresa:</strong> {ticket.nomeEmpresa}
        </Card.Text>
        <Card.Text className="small mb-1">
          <strong>Prioridade:</strong> {ticket.priority}
        </Card.Text>
        {ticket.assignedTo && (
          <Card.Text className="small mb-1">
            <strong>Responsável:</strong> {ticket.assignedTo}
          </Card.Text>
        )}
        <Card.Text className="small mb-1">
          <strong>Venc. Atendimento:</strong> {ticket.vencAtendimento}
        </Card.Text>
        <Card.Text className="small mb-1">
          <strong>Venc. Solução:</strong> {ticket.vencSolucao}
        </Card.Text>
        <Card.Text className="small mb-0">
          <strong>Status:</strong> {getStatusDisplay(ticket.status)}
        </Card.Text>
      </Card.Body>
    </Card>
  );
}
KanbanTicketCard.propTypes = { ticket: ticketShape.isRequired };

function KanbanColumn({
  title,
  tickets,
  className,
  innerRef,
  providedDroppableProps,
  headerClass,
}) {
  return (
    <Col
      md={3}
      lg={3}
      xl={2}
      className="mb-4"
      style={{ minWidth: '280px', maxWidth: '320px' }}
    >
      <Card className={`kanban-column h-100 ${className}`}>
        <Card.Header className={`fw-bold ${headerClass}`}>
          {title} ({tickets.length})
        </Card.Header>
        <Card.Body
          ref={innerRef}
          {...providedDroppableProps}
          style={{
            minHeight: '200px',
            maxHeight: '70vh',
            overflowY: 'auto',
            backgroundColor: '#f8f9fa',
          }}
        >
          {tickets.length > 0 ? (
            tickets.map((ticket, index) => (
              <Draggable
                key={String(ticket.id)}
                draggableId={String(ticket.id)}
                index={index}
              >
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                  >
                    <KanbanTicketCard ticket={ticket} />
                  </div>
                )}
              </Draggable>
            ))
          ) : (
            <p className="text-muted text-center mt-3 pt-3">
              Nenhum ticket aqui.
            </p>
          )}
          {/* providedDroppableProps.placeholder is not a valid prop; use provided.placeholder from react-beautiful-dnd */}
        </Card.Body>
      </Card>
    </Col>
  );
}
KanbanColumn.propTypes = {
  title: PropTypes.string.isRequired,
  tickets: PropTypes.arrayOf(ticketShape).isRequired,
  className: PropTypes.string,
  innerRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({
      current:
        typeof window === 'object'
          ? PropTypes.instanceOf(window.HTMLElement)
          : PropTypes.any,
    }),
  ]),
  providedDroppableProps: PropTypes.shape({}),
  headerClass: PropTypes.string,
};
KanbanColumn.defaultProps = {
  className: '',
  innerRef: null,
  providedDroppableProps: {},
  headerClass: 'bg-light text-dark',
};

// --- COMPONENTE PRINCIPAL ---
export default function Tickets() {
  const routeName = '/tickets';
  const titleName = 'Tickets';
  const navigate = useNavigate();
  const notification = useNotification();
  const [apiErrors, setApiErrors] = useState({});
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const userData = useSelector((state) => state.auth.user);
  const [viewMode, setViewMode] = useState('list');
  const [searchText, setSearchText] = useState('');
  // O texto que efetivamente vai para a API. Sem esse atraso, cada tecla
  // digitada dispara uma consulta completa no backend e as respostas chegam
  // fora de ordem, o que fazia a tela terminar vazia.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const requestIdRef = useRef(0);
  const [showOnlyOverdue, setShowOnlyOverdue] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState([
    'id',
    'title',
    'nomeEmpresa',
    'department',
    'assignedTo',
    'status',
    'origem',
  ]);

  const [ticketView, setTicketView] = useState('myTickets');

  // Estados para o novo Kanban baseado em mesas de serviço
  const [serviceDesks, setServiceDesks] = useState([]);
  const [selectedServiceDesk, setSelectedServiceDesk] = useState(null);
  const [kanbanMode, setKanbanMode] = useState('default'); // 'default' ou 'servicedesk'

  const statusMap = useMemo(() => {
    if (
      kanbanMode === 'servicedesk' &&
      selectedServiceDesk &&
      selectedServiceDesk.stages
    ) {
      // Mapear estágios da ServiceDesk selecionada
      const map = {};
      selectedServiceDesk.stages.forEach((stage) => {
        map[`column-stage-${stage.id}`] = stage.id;
      });
      return map;
    }
    // Mapeamento padrão de status
    return {
      'column-aberto': 'aberto',
      'column-em_atendimento': 'em_atendimento',
      'column-pausado': 'pausado',
    };
  }, [kanbanMode, selectedServiceDesk]);

  const reverseStatusMap = useMemo(() => {
    if (
      kanbanMode === 'servicedesk' &&
      selectedServiceDesk &&
      selectedServiceDesk.stages
    ) {
      // Mapear estágios da ServiceDesk selecionada (reverso)
      const map = {};
      selectedServiceDesk.stages.forEach((stage) => {
        map[stage.id] = `column-stage-${stage.id}`;
      });
      return map;
    }
    // Mapeamento reverso padrão de status
    return {
      aberto: 'column-aberto',
      em_atendimento: 'column-em_atendimento',
      pausado: 'column-pausado',
    };
  }, [kanbanMode, selectedServiceDesk]);

  const getData = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    setIsLoading(true);
    setApiErrors({});
    try {
      const params = new URLSearchParams();
      params.append('view', ticketView);
      params.append('userId', userData.id);
      // O backend já devolve apenas tickets em andamento; fechados e
      // cancelados nem trafegam.
      params.append('status', OPEN_STATUSES.join(','));

      if (debouncedSearch) {
        params.append('search', debouncedSearch);
      }

      const ticketsResponse = await axios.get(
        `${routeName}?${params.toString()}`
      );

      // Uma resposta antiga que chegue depois de uma nova não pode sobrescrever
      // o resultado atual da tela.
      if (requestIdRef.current !== requestId) return;

      // Sempre retorna array direto, sem paginação
      let rawTickets = [];
      if (ticketView === 'allTickets') {
        rawTickets =
          (ticketsResponse.data && ticketsResponse.data.tickets) ||
          ticketsResponse.data ||
          [];
      } else {
        rawTickets = ticketsResponse.data || [];
      }

      const formattedData = rawTickets.map((ticket) => {
        let vencAtendimento = 'N/A';
        let vencSolucao = 'N/A';
        const priorityInfo = ticket.Priority;
        if (priorityInfo) {
          const { atendimentoVencimento, solucaoVencimento } = priorityInfo;
          const currentTime = moment().tz('America/Sao_Paulo');
          if (atendimentoVencimento) {
            const createdTime = moment(ticket.created_at).tz(
              'America/Sao_Paulo'
            );
            const deadline = createdTime
              .clone()
              .add(moment.duration(atendimentoVencimento));
            if (ticket.startTime) {
              vencAtendimento = 'Atendido';
            } else {
              vencAtendimento = currentTime.isBefore(deadline)
                ? `Vence em ${deadline.fromNow(true)}`
                : 'Atrasado';
            }
          }
          if (solucaoVencimento && ticket.startTime) {
            if (ticket.closingTime) {
              vencSolucao = 'Solucionado';
            } else if (ticket.status === 'pausado') {
              vencSolucao = 'Pausado';
            } else {
              const slaDuration = moment.duration(solucaoVencimento);
              const deadline = moment(ticket.startTime).add(slaDuration);
              if (currentTime.isBefore(deadline)) {
                vencSolucao = `Vence em ${deadline.fromNow(true)}`;
              } else {
                vencSolucao = 'Atrasado';
              }
            }
          }
        }
        return {
          ...ticket,
          requester: ticket.requester
            ? ticket.requester.name
            : 'Não encontrado',
          assignedTo:
            ticket.AssignedUser && ticket.AssignedUser.UserProfile
              ? ticket.AssignedUser.UserProfile.name
              : 'Não atribuído',
          department: ticket.Department
            ? ticket.Department.name
            : 'Não disponível',
          priority: ticket.Priority ? ticket.Priority.name : 'Não definida',
          nomeEmpresa: ticket.Customer
            ? ticket.Customer.nomeEmpresa
            : 'Não disponível',
          createdAt: moment(ticket.created_at)
            .tz('America/Sao_Paulo')
            .format('DD/MM/YYYY HH:mm'),
          vencAtendimento,
          vencSolucao,
        };
      });

      // O recorte de SLA atrasado é feito sobre os dados já carregados
      // (ver visibleData), então alternar o botão não recarrega a lista.
      setData(formattedData);
    } catch (err) {
      if (requestIdRef.current !== requestId) return;
      console.error('Erro detalhado ao buscar dados dos tickets:', err);
      setApiErrors(err);
      setData([]);
    } finally {
      // Mantém o indicador de carregamento enquanto a requisição mais recente
      // ainda estiver em andamento.
      if (requestIdRef.current === requestId) {
        setIsLoading(false);
      }
    }
  }, [userData.id, routeName, ticketView, debouncedSearch]);

  const isOverdue = (ticket) =>
    ticket.vencAtendimento === 'Atrasado' || ticket.vencSolucao === 'Atrasado';

  const overdueCount = useMemo(() => data.filter(isOverdue).length, [data]);

  const visibleData = useMemo(
    () => (showOnlyOverdue ? data.filter(isOverdue) : data),
    [data, showOnlyOverdue]
  );

  // Só consulta o backend depois que o usuário para de digitar.
  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedSearch(searchText), 400);
    return () => clearTimeout(timeoutId);
  }, [searchText]);

  const loadServiceDesks = useCallback(async () => {
    try {
      const response = await axios.get('/servicedesks/kanban');
      setServiceDesks(response.data);
    } catch (error) {
      console.error('Erro ao carregar mesas de serviço:', error);
      notification('Erro ao carregar mesas de serviço', 'error');
    }
  }, [notification]);

  const updateTicketStatusFromKanban = useCallback(
    async (ticketId, newValue, oldValue) => {
      try {
        if (kanbanMode === 'servicedesk') {
          // Atualizar estágio do ticket
          await axios.put(`/tickets/${ticketId}`, { stageid: newValue });
          notification(
            `Ticket #${ticketId} movido para novo estágio!`,
            'success'
          );

          // Log da ação
          const oldStage = selectedServiceDesk.stages.find(
            (s) => s.id === oldValue
          );
          const newStage = selectedServiceDesk.stages.find(
            (s) => s.id === newValue
          );
          const userName =
            (userData.UserProfile && userData.UserProfile.name) || 'Usuário';

          if (oldStage && newStage) {
            await axios.post('/timeline', {
              ticket_id: ticketId,
              user_id: userData.id,
              user_name: userName,
              action: `${userName} moveu o ticket do estágio '${oldStage.name}' para '${newStage.name}'`,
            });
          }
        } else {
          // Modo padrão - atualizar status
          await axios.put(`/tickets/${ticketId}`, { status: newValue });
          notification(`Ticket #${ticketId} atualizado!`, 'success');
          let logAction = '';
          if (newValue === 'em_atendimento')
            logAction =
              oldValue === 'pausado'
                ? 'retomou o atendimento'
                : 'iniciou o atendimento';
          else if (newValue === 'pausado') logAction = 'pausou o atendimento';
          else if (newValue === 'fechado') logAction = 'encerrou o atendimento';
          else if (newValue === 'aberto')
            logAction = `alterou para status 'aberto'`;
          if (logAction) {
            const userName =
              (userData.UserProfile && userData.UserProfile.name) || 'Usuário';
            await axios.post('/timeline', {
              ticket_id: ticketId,
              user_id: userData.id,
              user_name: userName,
              action: `${userName} ${logAction}`,
            });
          }
        }
      } catch (error) {
        notification(`Erro ao atualizar o ticket: ${error.message}`, 'error');
        await getData();
      }
    },
    [userData, notification, getData, kanbanMode, selectedServiceDesk]
  );

  const onDragEnd = (result) => {
    const { destination, source, draggableId } = result;
    if (
      !destination ||
      (destination.droppableId === source.droppableId &&
        destination.index === source.index)
    )
      return;
    const ticketId = draggableId;
    if (!ticketId) return;
    const newValue = statusMap[destination.droppableId];
    if (!newValue) return;
    const oldValue = statusMap[source.droppableId];
    if (newValue === oldValue) return;
    const ticketToMove = data.find((t) => String(t.id) === ticketId);
    if (ticketToMove) {
      if (kanbanMode === 'servicedesk') {
        // Atualizar estágio do ticket
        setData(
          data.map((t) =>
            String(t.id) === ticketId ? { ...t, stageid: newValue } : t
          )
        );
      } else {
        // Atualizar status do ticket
        setData(
          data.map((t) =>
            String(t.id) === ticketId ? { ...t, status: newValue } : t
          )
        );
      }
    }
    updateTicketStatusFromKanban(ticketId, newValue, oldValue);
  };

  useEffect(() => {
    getData();
  }, [getData]);

  useEffect(() => {
    loadServiceDesks();
  }, [loadServiceDesks]);

  // Removido useEffect de paginação

  const allColumns = useMemo(
    () => [
      { Header: 'Nº Ticket', accessor: 'id', Cell: TicketNumberCell },
      { Header: 'Título', accessor: 'title' },
      { Header: 'Solicitante', accessor: 'requester' },
      { Header: 'Empresa', accessor: 'nomeEmpresa' },
      { Header: 'Prioridade', accessor: 'priority' },
      { Header: 'Departamento', accessor: 'department' },
      { Header: 'Responsável', accessor: 'assignedTo' },
      { Header: 'Criado em', accessor: 'createdAt' },
      { Header: 'Venc Atendimento', accessor: 'vencAtendimento' },
      { Header: 'Venc Solução', accessor: 'vencSolucao' },
      { Header: 'Status', accessor: 'status' },
      { Header: 'Origem', accessor: 'origem' },
      { Header: 'Ações', accessor: 'actions' },
    ],
    []
  );

  const tableColumns = useMemo(
    () => allColumns.filter((col) => selectedColumns.includes(col.accessor)),
    [selectedColumns, allColumns]
  );

  const handleColumnSelection = (e) => {
    const { value, checked } = e.target;
    setSelectedColumns((prev) =>
      checked ? [...prev, value] : prev.filter((col) => col !== value)
    );
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'aberto':
        return <b className="text-success">Aberto</b>;
      case 'em_atendimento':
        return <b className="text-warning">Em Atendimento</b>;
      case 'pausado':
        return <b className="text-primary">Em Pausa</b>;
      case 'fechado':
        return <b className="text-danger">Fechado</b>;
      default:
        return <b>{status}</b>;
    }
  };

  const tableData = useMemo(() => {
    return visibleData.map((ticketObj) => ({
      ...ticketObj,
      status: getStatusBadge(ticketObj.status),
      actions: (
        <CardHeaderMore
          links={[
            {
              onclick: () => navigate(`${routeName}/${ticketObj.id}/edit`),
              faicon: 'faEdit',
              text: 'Detalhes',
            },
          ]}
        />
      ),
    }));
  }, [visibleData, navigate, routeName]);

  const ticketsByStatusForKanban = useMemo(() => {
    if (kanbanMode === 'servicedesk' && selectedServiceDesk) {
      // Agrupar tickets por estágio
      return visibleData.reduce((acc, ticket) => {
        const stageId =
          ticket.stageid || (ticket.Stage ? ticket.Stage.id : null);
        const columnId = reverseStatusMap[stageId];
        if (columnId) {
          if (!acc[columnId]) acc[columnId] = [];
          acc[columnId].push(ticket);
        }
        return acc;
      }, {});
    }
    // Modo padrão - agrupar por status
    return visibleData.reduce((acc, ticket) => {
      const columnId = reverseStatusMap[ticket.status];
      if (columnId) {
        if (!acc[columnId]) acc[columnId] = [];
        acc[columnId].push(ticket);
      }
      return acc;
    }, {});
  }, [visibleData, reverseStatusMap, kanbanMode, selectedServiceDesk]);

  // Função de paginação removida

  return (
    <Container fluid className="px-lg-4 px-xl-5">
      <Loading isLoading={isLoading} />
      <Breadcrumbs title={titleName} />
      <div className="page-header d-flex flex-wrap justify-content-between align-items-center mb-3">
        <h1 className="page-heading">{titleName}</h1>
        <div className="d-flex align-items-center">
          <Button
            variant={ticketView === 'myTickets' ? 'primary' : 'outline-primary'}
            onClick={() => setTicketView('myTickets')}
            className="me-2"
          >
            Meus Tickets
          </Button>
          <Button
            variant={
              ticketView === 'allTickets' ? 'primary' : 'outline-primary'
            }
            onClick={() => setTicketView('allTickets')}
            className="me-3"
          >
            Todos os Tickets
          </Button>

          <Button
            variant={viewMode === 'list' ? 'secondary' : 'outline-secondary'}
            onClick={() => setViewMode('list')}
            className="me-2"
          >
            Lista
          </Button>
          <Button
            variant={viewMode === 'kanban' ? 'secondary' : 'outline-secondary'}
            onClick={() => {
              setViewMode('kanban');
              if (serviceDesks.length === 0) {
                loadServiceDesks();
              }
            }}
            className="me-3"
          >
            Kanban
          </Button>

          <Button
            variant={showOnlyOverdue ? 'danger' : 'outline-danger'}
            onClick={() => setShowOnlyOverdue((prev) => !prev)}
            className="me-3"
          >
            SLA Atrasados
            {showOnlyOverdue && (
              <span className="badge bg-light text-danger ms-2">
                {overdueCount}
              </span>
            )}
          </Button>

          <Link to={`${routeName}/new`}>
            <Button variant="primary" className="text-uppercase">
              <FaPlus /> Novo Ticket
            </Button>
          </Link>
        </div>
      </div>

      {/* Seletor de Mesa de Serviço para Kanban */}
      {viewMode === 'kanban' && (
        <div className="d-flex align-items-center gap-3 mb-3">
          <div className="d-flex gap-2">
            <Button
              variant={kanbanMode === 'default' ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={() => {
                setKanbanMode('default');
                setSelectedServiceDesk(null);
              }}
            >
              Status Padrão
            </Button>
            <Button
              variant={
                kanbanMode === 'servicedesk' ? 'primary' : 'outline-primary'
              }
              size="sm"
              onClick={() => setKanbanMode('servicedesk')}
            >
              Mesa de Serviço
            </Button>
          </div>

          {kanbanMode === 'servicedesk' && (
            <select
              className="form-select"
              style={{ width: '250px' }}
              value={selectedServiceDesk ? selectedServiceDesk.id : ''}
              onChange={(e) => {
                const deskId = e.target.value;
                const desk = serviceDesks.find((d) => String(d.id) === deskId);
                setSelectedServiceDesk(desk || null);
              }}
            >
              <option value="">Selecione uma Mesa de Serviço</option>
              {serviceDesks.map((desk) => (
                <option key={desk.id} value={desk.id}>
                  {desk.displayName || desk.name}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      <Form.Group controlId="search" className="mb-3">
        <strong>Pesquisar por número ou título do ticket</strong>
        <Form.Control
          type="search"
          placeholder="Digite o número (#123), título, responsável, solicitante, cliente ou empresa..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ maxWidth: '400px' }}
        />
      </Form.Group>

      {viewMode === 'list' && (
        <Card className="mb-3">
          <Card.Body>
            <Form.Label>Exibir colunas:</Form.Label>
            <div>
              {allColumns.map(
                (col) =>
                  col.accessor && (
                    <Form.Check
                      inline
                      key={col.accessor}
                      label={col.Header}
                      value={col.accessor}
                      checked={selectedColumns.includes(col.accessor)}
                      onChange={handleColumnSelection}
                    />
                  )
              )}
            </div>
          </Card.Body>
        </Card>
      )}

      <section>
        <ErrorProcessor error={apiErrors} />
        {viewMode === 'list' ? (
          <Card className="card-table">
            <div className="table-responsive" style={{ overflowX: 'auto' }}>
              <DataTable
                className="table table-hover table-striped"
                items={tableData}
                columns={tableColumns}
                defaultPageSize={20}
              />
            </div>
          </Card>
        ) : (
          <DragDropContext onDragEnd={onDragEnd}>
            {kanbanMode === 'servicedesk' && selectedServiceDesk ? (
              // Layout com barra de rolagem para Mesa de Serviço
              <div style={{ overflowX: 'auto', paddingBottom: '10px' }}>
                <Row
                  className="g-3"
                  style={{ flexWrap: 'nowrap', minWidth: 'max-content' }}
                >
                  {(() => {
                    if (kanbanMode === 'servicedesk' && selectedServiceDesk) {
                      // Renderizar colunas baseadas nos estágios da mesa selecionada
                      if (
                        selectedServiceDesk.stages &&
                        selectedServiceDesk.stages.length > 0
                      ) {
                        return selectedServiceDesk.stages.map((stage) => {
                          const droppableId = `column-stage-${stage.id}`;
                          const tickets =
                            ticketsByStatusForKanban[droppableId] || [];
                          return (
                            <Col
                              md={3}
                              lg={3}
                              xl={2}
                              key={droppableId}
                              style={{
                                minWidth: '280px',
                                maxWidth: '320px',
                                marginRight: '15px',
                              }}
                            >
                              <Droppable
                                droppableId={droppableId}
                                key={droppableId}
                              >
                                {(provided) => (
                                  <KanbanColumn
                                    title={stage.name}
                                    tickets={tickets}
                                    headerClass="bg-info text-white"
                                    innerRef={provided.innerRef}
                                    providedDroppableProps={{
                                      ...provided.droppableProps,
                                      placeholder: provided.placeholder,
                                    }}
                                  />
                                )}
                              </Droppable>
                            </Col>
                          );
                        });
                      }
                      return (
                        <div>
                          Nenhum estágio encontrado para esta mesa de serviço.
                        </div>
                      );
                    }
                    return null;
                  })()}
                </Row>
              </div>
            ) : (
              // Layout normal para Status (sem barra de rolagem)
              <Row className="g-3">
                {(() => {
                  // Renderizar colunas baseadas nos status padrão
                  return Object.keys(statusMap).map((droppableId) => {
                    const status = statusMap[droppableId];
                    const title = status
                      .replace(/_/g, ' ')
                      .replace(/\b\w/g, (l) => l.toUpperCase());
                    const tickets = ticketsByStatusForKanban[droppableId] || [];
                    let headerClass = 'bg-light text-dark';
                    if (status === 'aberto')
                      headerClass = 'bg-success text-white';
                    if (status === 'em_atendimento')
                      headerClass = 'bg-warning text-dark';
                    if (status === 'pausado')
                      headerClass = 'bg-primary text-white';
                    return (
                      <Col md={4} lg={4} xl={4} key={droppableId}>
                        <Droppable droppableId={droppableId} key={droppableId}>
                          {(provided) => (
                            <KanbanColumn
                              title={title}
                              tickets={tickets}
                              headerClass={headerClass}
                              innerRef={provided.innerRef}
                              providedDroppableProps={{
                                ...provided.droppableProps,
                                placeholder: provided.placeholder,
                              }}
                            />
                          )}
                        </Droppable>
                      </Col>
                    );
                  });
                })()}
              </Row>
            )}
          </DragDropContext>
        )}
      </section>

      {/* Paginação removida conforme solicitado */}
    </Container>
  );
}
